import { AccountPage, api, type AuthConfig, Empty, ExtranetShell, go, LoginPage, NavGroup, NavLink, roleSummary, setUnauthorizedHandler, useAsync, UsersPage, useRoute, type Me } from "@getaways/extranet-ui";
import { IconChecklist, IconHome, IconMap, IconRoute, IconScale, IconSettings, IconTicket, IconUsers } from "@tabler/icons-react";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { canWith, MeContext, ROLE_NAMES, rolesFor } from "./lib/access";
import { BookingPage } from "./pages/Booking";
import { BookingsPage } from "./pages/Bookings";
import { CataloguePage } from "./pages/Catalogue";
import { OverviewPage } from "./pages/Overview";
import { ReconciliationPage } from "./pages/Reconciliation";
import { SellPage } from "./pages/Sell";
import { SuppliersPage } from "./pages/Suppliers";
import { TasksPage } from "./pages/Tasks";

export interface Overview {
  mode: "demo" | "live_read" | "live";
  heldBookings: number;
  confirmedBookings: number;
  cancellationsPending: number;
  openTasks: number;
  tasksByKind: Record<string, number>;
  tours: number;
  bookableSuppliers: string[];
  openReconciliationIssues: number;
}

export function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [auth, setAuth] = useState<AuthConfig>({ mode: "local" });
  const route = useRoute();
  const page = route[0] ?? "overview";

  const start = useCallback(async () => {
    await api<AuthConfig>("GET", "/auth/config").then(setAuth).catch(() => undefined);
    try {
      const who = await api<Me | { service: true }>("GET", "/auth/me");
      // Without a session in development (API without a key) the request counts as a service call: still ask for a login.
      setMe("service" in who ? null : who);
    } catch {
      setMe(null);
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => setMe(null));
    void start();
  }, [start]);

  const overview = useAsync(() => (me ? api<Overview>("GET", "/v1/admin/overview") : Promise.resolve(undefined)), [me, route.join("/")]);

  if (me === undefined) return <div className="empty">Loading…</div>;
  if (me === null) return <LoginPage product="Resosys Tours" headline="Multi-day tours from search to departure, in one place." tagline="The Resosys Tours back-office for the Ideastation team." onLoggedIn={start} auth={auth} />;

  const logout = async () => {
    const res = await api<{ redirect?: string }>("POST", "/auth/logout").catch(() => undefined);
    if (res?.redirect) return window.location.assign(res.redirect);
    setMe(null);
    go("overview");
  };
  const manageUsers = canWith(me, "users:manage") && auth.mode === "local";

  let content: ReactNode;
  if (page === "overview") content = <OverviewPage overview={overview.data} />;
  else if (page === "sell") content = <SellPage />;
  else if (page === "bookings" && route[1]) content = <BookingPage id={route[1]} />;
  else if (page === "bookings") content = <BookingsPage />;
  else if (page === "tasks") content = <TasksPage />;
  else if (page === "suppliers") content = <SuppliersPage />;
  else if (page === "catalogue") content = <CataloguePage />;
  else if (page === "reconciliation") content = <ReconciliationPage />;
  else if (page === "users") content = manageUsers ? <UsersPage me={me.user} roleNames={ROLE_NAMES} rolesFor={rolesFor} resources={[]} resourceKey="resourceIds" resourceLabel="Resources" /> : <Empty>Administrators only.</Empty>;
  else if (page === "conta") content = <AccountPage user={me.user} roleNames={ROLE_NAMES} auth={auth} />;
  else content = <Empty>Page not found.</Empty>;

  const o = overview.data;
  const nav = (
    <>
      <NavLink to="overview" page={page} icon={<IconHome size={18} />} label="Overview" />
      {canWith(me, "booking:manage") && <NavLink to="sell" page={page} icon={<IconRoute size={18} />} label="Sell a tour" />}
      <NavLink to="bookings" page={page} icon={<IconTicket size={18} />} label="Bookings" />
      <NavLink to="tasks" page={page} icon={<IconChecklist size={18} />} label="Tasks" count={o?.openTasks} />
      <NavGroup>Setup</NavGroup>
      <NavLink to="suppliers" page={page} icon={<IconSettings size={18} />} label="Suppliers and markets" />
      <NavLink to="catalogue" page={page} icon={<IconMap size={18} />} label="Catalogue" />
      <NavLink to="reconciliation" page={page} icon={<IconScale size={18} />} label="Reconciliation" count={o?.openReconciliationIssues} />
      {manageUsers && <NavLink to="users" page={page} icon={<IconUsers size={18} />} label="Users" />}
    </>
  );

  return (
    <MeContext.Provider value={me}>
      <ExtranetShell product="Resosys Tours · back-office" user={me.user} role={roleSummary(me.user.memberships, ROLE_NAMES)} onLogout={() => void logout()} portalUrl={auth.mode === "central" ? auth.portalUrl : undefined} nav={nav}>
        {content}
      </ExtranetShell>
    </MeContext.Provider>
  );
}
